/**
 * Report Server Routes — HTTP route handlers for the report server.
 *
 * Each handler reads data from the streaming report directory on disk
 * and returns JSON responses. Pagination, filtering, search, and sort
 * are supported for the test index endpoint.
 */

import { readFileSync, existsSync, readdirSync, statSync, rmSync, createReadStream } from 'node:fs';
import { join, extname } from 'node:path';
import type { IncomingMessage, ServerResponse, Server } from 'node:http';
import type { TestIndexEntry, StreamingReportSummary, TestDetailData } from '@testrelic/core';
import { readJsonlPage } from './jsonl-stream.js';

const SAFE_ID_PATTERN = /^[a-f0-9]{12}$/;
const TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2}(-\d+)?$/;
const MAX_PAGE_SIZE = 500;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function sendJson(res: ServerResponse, status: number, body: unknown): void {
  res.writeHead(status, { 'Content-Type': 'application/json' });
  res.end(JSON.stringify(body));
}

export function setCorsHeaders(res: ServerResponse): void {
  res.setHeader('Access-Control-Allow-Origin', '*');
  res.setHeader('Access-Control-Allow-Methods', 'GET, DELETE, OPTIONS');
  res.setHeader('Access-Control-Allow-Headers', 'Content-Type');
}

function readJsonFile<T>(filePath: string): T | null {
  try {
    if (!existsSync(filePath)) return null;
    return JSON.parse(readFileSync(filePath, 'utf-8')) as T;
  } catch {
    return null;
  }
}

function getDirSize(dirPath: string): number {
  let size = 0;
  try {
    const entries = readdirSync(dirPath, { withFileTypes: true });
    for (const entry of entries) {
      const fullPath = join(dirPath, entry.name);
      if (entry.isFile()) {
        size += statSync(fullPath).size;
      } else if (entry.isDirectory()) {
        size += getDirSize(fullPath);
      }
    }
  } catch { /* skip */ }
  return size;
}

// ---------------------------------------------------------------------------
// Route Handlers
// ---------------------------------------------------------------------------

/** GET /api/health */
export function handleHealth(
  _req: IncomingMessage,
  res: ServerResponse,
  reportDir: string,
  startTime: number,
): void {
  const index = readJsonFile<TestIndexEntry[]>(join(reportDir, 'index.json'));
  sendJson(res, 200, {
    status: 'ok',
    reportMode: 'streaming',
    testCount: index?.length ?? 0,
    uptime: Math.floor((Date.now() - startTime) / 1000),
  });
}

/** GET /api/summary */
export function handleSummary(
  _req: IncomingMessage,
  res: ServerResponse,
  reportDir: string,
): void {
  const summary = readJsonFile<StreamingReportSummary>(join(reportDir, 'summary.json'));
  if (!summary) {
    sendJson(res, 404, { error: 'Summary not found' });
    return;
  }
  sendJson(res, 200, summary);
}

/** GET /api/tests — paginated, filterable, searchable, sortable test index. */
export function handleTests(
  req: IncomingMessage,
  res: ServerResponse,
  reportDir: string,
): void {
  const index = readJsonFile<TestIndexEntry[]>(join(reportDir, 'index.json'));
  if (!index) {
    sendJson(res, 404, { error: 'Test index not found' });
    return;
  }

  const url = new URL(req.url ?? '/', `http://${req.headers.host}`);
  const params = url.searchParams;

  // Parse query parameters
  const page = Math.max(1, parseInt(params.get('page') ?? '1', 10) || 1);
  const pageSize = Math.min(MAX_PAGE_SIZE, Math.max(1, parseInt(params.get('pageSize') ?? '100', 10) || 100));
  const statusFilter = params.get('status')?.split(',').filter(Boolean) ?? null;
  const fileFilter = params.get('file') ?? null;
  const searchQuery = params.get('search')?.toLowerCase() ?? null;
  const tagFilter = params.get('tag')?.split(',').filter(Boolean) ?? null;
  const sortField = params.get('sort') ?? 'file';
  const sortOrder = params.get('order') === 'desc' ? -1 : 1;

  // Filter
  let filtered = index;
  if (statusFilter && statusFilter.length > 0) {
    filtered = filtered.filter((t) => statusFilter.includes(t.status));
  }
  if (fileFilter) {
    filtered = filtered.filter((t) => t.filePath === fileFilter || t.filePath.startsWith(fileFilter + '/'));
  }
  if (searchQuery) {
    filtered = filtered.filter((t) =>
      t.title.toLowerCase().includes(searchQuery) ||
      t.filePath.toLowerCase().includes(searchQuery),
    );
  }
  if (tagFilter && tagFilter.length > 0) {
    filtered = filtered.filter((t) => tagFilter.some((tag) => t.tags.includes(tag)));
  }

  // Sort
  filtered = [...filtered].sort((a, b) => {
    let cmp = 0;
    switch (sortField) {
      case 'duration': cmp = a.duration - b.duration; break;
      case 'status': cmp = a.status.localeCompare(b.status); break;
      case 'title': cmp = a.title.localeCompare(b.title); break;
      case 'file':
      default: cmp = a.filePath.localeCompare(b.filePath); break;
    }
    return cmp * sortOrder;
  });

  // Paginate
  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIdx = (page - 1) * pageSize;
  const tests = filtered.slice(startIdx, startIdx + pageSize);

  sendJson(res, 200, {
    tests,
    pagination: { page, pageSize, totalItems, totalPages },
    filters: {
      status: statusFilter,
      file: fileFilter,
      search: searchQuery,
      tag: tagFilter,
    },
  });
}

/** GET /api/tests/:id — metadata for a single test (lightweight). */
export function handleTestDetail(
  _req: IncomingMessage,
  res: ServerResponse,
  reportDir: string,
  testId: string,
): void {
  // Path traversal protection
  if (!SAFE_ID_PATTERN.test(testId)) {
    sendJson(res, 400, { error: 'Invalid test ID format' });
    return;
  }

  // v3 directory format: tests/<testId>/meta.json
  const metaPath = join(reportDir, 'tests', testId, 'meta.json');
  // Fallback: v2 single-file format: tests/<testId>.json
  const legacyPath = join(reportDir, 'tests', `${testId}.json`);

  const filePath = existsSync(metaPath) ? metaPath : existsSync(legacyPath) ? legacyPath : null;
  if (!filePath) {
    sendJson(res, 404, { error: `Test not found: ${testId}` });
    return;
  }

  try {
    const data = readFileSync(filePath, 'utf-8');
    res.writeHead(200, { 'Content-Type': 'application/json' });
    res.end(data);
  } catch (err) {
    sendJson(res, 500, { error: err instanceof Error ? err.message : String(err) });
  }
}

/** GET /api/tests/:id/network — paginated network requests from JSONL. */
export async function handleTestDataFile(
  req: IncomingMessage,
  res: ServerResponse,
  reportDir: string,
  testId: string,
  dataType: 'network' | 'console' | 'api-calls',
): Promise<void> {
  if (!SAFE_ID_PATTERN.test(testId)) {
    sendJson(res, 400, { error: 'Invalid test ID format' });
    return;
  }

  const fileNames: Record<string, string> = {
    'network': 'network.jsonl',
    'console': 'console.jsonl',
    'api-calls': 'api-calls.jsonl',
  };

  const jsonlPath = join(reportDir, 'tests', testId, fileNames[dataType]);
  if (!existsSync(jsonlPath)) {
    sendJson(res, 200, { items: [], total: 0, page: 1, pageSize: 50, totalPages: 0 });
    return;
  }

  try {
    const url = new URL(req.url ?? '/', `http://${req.headers.host}`);
    const params = url.searchParams;
    const page = Math.max(1, parseInt(params.get('page') ?? '1', 10) || 1);
    const pageSize = Math.min(MAX_PAGE_SIZE, Math.max(1, parseInt(params.get('pageSize') ?? '50', 10) || 50));

    // Read known total from meta.json if available
    let knownTotal: number | undefined;
    const metaPath = join(reportDir, 'tests', testId, 'meta.json');
    if (existsSync(metaPath)) {
      try {
        const meta = JSON.parse(readFileSync(metaPath, 'utf-8')) as TestDetailData;
        switch (dataType) {
          case 'network': knownTotal = meta.networkRequestsCount; break;
          case 'console': knownTotal = meta.consoleLogsCount; break;
          case 'api-calls': knownTotal = meta.apiCallsCount; break;
        }
      } catch { /* use line counting fallback */ }
    }

    const result = await readJsonlPage(jsonlPath, page, pageSize, knownTotal);
    sendJson(res, 200, result);
  } catch (err) {
    sendJson(res, 500, { error: err instanceof Error ? err.message : String(err) });
  }
}

/** GET /api/files — per-file test aggregation. */
export function handleFiles(
  _req: IncomingMessage,
  res: ServerResponse,
  reportDir: string,
): void {
  const index = readJsonFile<TestIndexEntry[]>(join(reportDir, 'index.json'));
  if (!index) {
    sendJson(res, 404, { error: 'Test index not found' });
    return;
  }

  const fileMap = new Map<string, { total: number; passed: number; failed: number; flaky: number; skipped: number; timedOut: number }>();
  for (const t of index) {
    if (t.isRetry) continue; // Only count final attempts
    let stats = fileMap.get(t.filePath);
    if (!stats) {
      stats = { total: 0, passed: 0, failed: 0, flaky: 0, skipped: 0, timedOut: 0 };
      fileMap.set(t.filePath, stats);
    }
    stats.total++;
    switch (t.status) {
      case 'passed': stats.passed++; break;
      case 'failed': stats.failed++; break;
      case 'flaky': stats.flaky++; break;
      case 'skipped': stats.skipped++; break;
      case 'timedout': stats.timedOut++; break;
    }
  }

  const files = Array.from(fileMap.entries())
    .map(([filePath, s]) => ({ filePath, ...s }))
    .sort((a, b) => a.filePath.localeCompare(b.filePath));

  sendJson(res, 200, { files });
}

/** GET /api/artifacts — list artifact runs. */
export function handleArtifactsList(
  _req: IncomingMessage,
  res: ServerResponse,
  artifactsDir: string,
): void {
  if (!existsSync(artifactsDir)) {
    sendJson(res, 200, { runs: [], totalSizeBytes: 0 });
    return;
  }
  try {
    const runs: Array<{ folderName: string; totalSizeBytes: number; testCount: number }> = [];
    let totalSizeBytes = 0;
    const entries = readdirSync(artifactsDir, { withFileTypes: true });
    for (const entry of entries) {
      if (!entry.isDirectory() || !TIMESTAMP_PATTERN.test(entry.name)) continue;
      const runDir = join(artifactsDir, entry.name);
      const size = getDirSize(runDir);
      const testDirs = readdirSync(runDir, { withFileTypes: true }).filter((e) => e.isDirectory());
      runs.push({ folderName: entry.name, totalSizeBytes: size, testCount: testDirs.length });
      totalSizeBytes += size;
    }
    runs.sort((a, b) => b.folderName.localeCompare(a.folderName));
    sendJson(res, 200, { runs, totalSizeBytes });
  } catch (err) {
    sendJson(res, 500, { error: err instanceof Error ? err.message : String(err) });
  }
}

/** DELETE /api/artifacts — delete all artifact runs. */
export function handleArtifactsDeleteAll(
  _req: IncomingMessage,
  res: ServerResponse,
  artifactsDir: string,
): void {
  try {
    let deletedCount = 0;
    let freedBytes = 0;
    if (existsSync(artifactsDir)) {
      const entries = readdirSync(artifactsDir, { withFileTypes: true });
      for (const entry of entries) {
        if (!entry.isDirectory() || !TIMESTAMP_PATTERN.test(entry.name)) continue;
        const runDir = join(artifactsDir, entry.name);
        const size = getDirSize(runDir);
        rmSync(runDir, { recursive: true, force: true });
        freedBytes += size;
        deletedCount++;
      }
    }
    sendJson(res, 200, { deletedCount, freedBytes });
  } catch (err) {
    sendJson(res, 500, { error: err instanceof Error ? err.message : String(err) });
  }
}

/** DELETE /api/artifacts/:folderName — delete a single artifact run. */
export function handleArtifactDelete(
  _req: IncomingMessage,
  res: ServerResponse,
  artifactsDir: string,
  folderName: string,
): void {
  if (!TIMESTAMP_PATTERN.test(folderName)) {
    sendJson(res, 400, { error: 'Invalid folder name' });
    return;
  }
  const runDir = join(artifactsDir, folderName);
  try {
    const stat = statSync(runDir);
    if (!stat.isDirectory()) {
      sendJson(res, 404, { error: 'Not found' });
      return;
    }
  } catch {
    sendJson(res, 404, { error: 'Not found' });
    return;
  }
  try {
    const freedBytes = getDirSize(runDir);
    rmSync(runDir, { recursive: true, force: true });
    sendJson(res, 200, { deleted: folderName, freedBytes });
  } catch (err) {
    sendJson(res, 500, { error: err instanceof Error ? err.message : String(err) });
  }
}

/** POST /api/shutdown — gracefully shut down the server. */
export function handleShutdown(
  _req: IncomingMessage,
  res: ServerResponse,
  server: Server,
): void {
  sendJson(res, 200, { status: 'shutting_down' });
  server.close();
}

/**
 * Serve a static file from disk, honouring a byte `Range`.
 *
 * A `<video>` element asks for `Range: bytes=0-` before it will play. This
 * used to answer every request with a flat `200` and the whole body, which
 * Chrome treats as a protocol violation for media: the element sat in
 * NETWORK_LOADING with readyState 0, no error event, and a permanent spinner.
 * Recordings were captured, copied and linked correctly — and never played.
 *
 * Files are streamed rather than read whole: a screencast is routinely tens of
 * megabytes and there is no reason to hold one in memory per request.
 */
export function serveStaticFile(
  req: IncomingMessage,
  res: ServerResponse,
  filePath: string,
): void {
  if (!existsSync(filePath)) {
    sendJson(res, 404, { error: 'File not found' });
    return;
  }
  try {
    const size = statSync(filePath).size;
    const ext = extname(filePath).toLowerCase();
    const contentType = MIME_TYPES[ext] ?? 'application/octet-stream';
    const range = parseByteRange(req.headers.range, size);

    if (range === 'unsatisfiable') {
      res.writeHead(416, { 'Content-Range': `bytes */${size}`, 'Accept-Ranges': 'bytes' });
      res.end();
      return;
    }

    if (range) {
      res.writeHead(206, {
        'Content-Type': contentType,
        'Content-Length': range.end - range.start + 1,
        'Content-Range': `bytes ${range.start}-${range.end}/${size}`,
        'Accept-Ranges': 'bytes',
      });
      pipeOrFail(createReadStream(filePath, { start: range.start, end: range.end }), res);
      return;
    }

    res.writeHead(200, {
      'Content-Type': contentType,
      'Content-Length': size,
      'Accept-Ranges': 'bytes',
    });
    pipeOrFail(createReadStream(filePath), res);
  } catch (err) {
    sendJson(res, 500, { error: err instanceof Error ? err.message : String(err) });
  }
}

/**
 * Pipe a read stream to the response, surviving a failure mid-stream.
 *
 * The previous implementation read the whole file synchronously inside a
 * `try`, so any read error became a 500. Streaming moves the failure to an
 * async `'error'` event, and an unhandled one on a stream is rethrown by Node
 * as an uncaught exception — which kills the `serve` process and every other
 * tab viewing the report. It is reachable: a video whose file is deleted by the
 * run-delete endpoint mid-request, or a `.webm` still locked by another
 * process on Windows.
 *
 * Headers are already sent by this point, so there is no status left to send;
 * the honest outcome is to abort the response and keep the server up.
 */
function pipeOrFail(stream: ReturnType<typeof createReadStream>, res: ServerResponse): void {
  stream.on('error', () => {
    stream.destroy();
    res.destroy();
  });
  res.on('close', () => { stream.destroy(); });
  stream.pipe(res);
}

/**
 * Parse a single-range `Range` header against a known file size.
 *
 * Returns null when there is nothing to honour (absent, or a form we do not
 * serve such as a multi-range request), and 'unsatisfiable' when the range
 * falls outside the file so the caller can answer 416.
 */
export function parseByteRange(
  header: string | undefined,
  size: number,
): { start: number; end: number } | 'unsatisfiable' | null {
  if (!header) return null;
  const match = /^bytes=(\d*)-(\d*)$/.exec(header.trim());
  if (!match) return null;

  const [, rawStart, rawEnd] = match;
  if (rawStart === '' && rawEnd === '') return null;

  let start: number;
  let end: number;
  if (rawStart === '') {
    // A suffix range: the last N bytes.
    const suffix = Number(rawEnd);
    if (suffix <= 0) return 'unsatisfiable';
    start = Math.max(0, size - suffix);
    end = size - 1;
  } else {
    start = Number(rawStart);
    end = rawEnd === '' ? size - 1 : Math.min(Number(rawEnd), size - 1);
  }

  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  if (size === 0 || start >= size || start > end) return 'unsatisfiable';
  return { start, end };
}

const MIME_TYPES: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.webm': 'video/webm',
  '.mp4': 'video/mp4',
  '.json': 'application/json',
  '.html': 'text/html',
  '.css': 'text/css',
  '.js': 'text/javascript',
  '.svg': 'image/svg+xml',
};
